// ── Replay UI ──
// Timeline overlay for replay playback. Shows timeline bar, play/pause,
// speed controls, round markers, and player list for camera follow.

import { type ReplayPlayer, type RoundMarker } from './replay-player';
import { type ReplayRenderer } from './replay-renderer';

const SPEEDS = [0.25, 0.5, 1, 2, 4];
const SKIP_SECONDS = 5;

export class ReplayUI {
  private container: HTMLDivElement;
  private timelineBar: HTMLDivElement;
  private timelineFill: HTMLDivElement;
  private timelineHandle: HTMLDivElement;
  private playPauseBtn: HTMLButtonElement;
  private speedDisplay: HTMLSpanElement;
  private timeDisplay: HTMLSpanElement;
  private playerList: HTMLDivElement;
  private roundMarkerContainer: HTMLDivElement;

  private replayPlayer: ReplayPlayer;
  private renderer: ReplayRenderer | null = null;
  private visible = false;
  private exitCallback: (() => void) | null = null;

  // Keyboard handler reference (for removal)
  private _onKeyDown: (e: KeyboardEvent) => void;

  // Timeline dragging
  private dragging = false;
  private _onMouseMove: (e: MouseEvent) => void;
  private _onMouseUp: (e: MouseEvent) => void;

  constructor(replayPlayer: ReplayPlayer) {
    this.replayPlayer = replayPlayer;

    // Create container
    this.container = document.createElement('div');
    this.container.id = 'replay-ui';
    this.container.style.cssText = `
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      height: 80px;
      background: linear-gradient(transparent, rgba(0,0,0,0.85));
      display: none;
      flex-direction: column;
      align-items: center;
      justify-content: flex-end;
      padding: 8px 20px 12px;
      z-index: 1000;
      font-family: 'Segoe UI', Arial, sans-serif;
      user-select: none;
      pointer-events: auto;
    `;

    // Controls row
    const controlsRow = document.createElement('div');
    controlsRow.style.cssText = `
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      max-width: 900px;
      margin-bottom: 6px;
    `;

    // Play/Pause button
    this.playPauseBtn = document.createElement('button');
    this.playPauseBtn.textContent = '\u25B6'; // play triangle
    this.playPauseBtn.style.cssText = `
      background: rgba(255,255,255,0.15);
      border: 1px solid rgba(255,255,255,0.3);
      color: #fff;
      font-size: 16px;
      width: 36px;
      height: 36px;
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    this.playPauseBtn.addEventListener('click', () => this.togglePlayPause());

    // Speed controls
    const speedGroup = document.createElement('div');
    speedGroup.style.cssText = 'display: flex; align-items: center; gap: 4px;';

    const speedDownBtn = this.createSmallButton('\u25C0');
    speedDownBtn.addEventListener('click', () => this.cycleSpeed(-1));

    this.speedDisplay = document.createElement('span');
    this.speedDisplay.textContent = '1x';
    this.speedDisplay.style.cssText = `
      color: #ffd700;
      font-size: 13px;
      font-weight: bold;
      min-width: 32px;
      text-align: center;
    `;

    const speedUpBtn = this.createSmallButton('\u25B6');
    speedUpBtn.addEventListener('click', () => this.cycleSpeed(1));

    speedGroup.appendChild(speedDownBtn);
    speedGroup.appendChild(this.speedDisplay);
    speedGroup.appendChild(speedUpBtn);

    // Time display
    this.timeDisplay = document.createElement('span');
    this.timeDisplay.textContent = '0:00 / 0:00';
    this.timeDisplay.style.cssText = `
      color: rgba(255,255,255,0.8);
      font-size: 12px;
      min-width: 100px;
      text-align: center;
    `;

    // Spacer
    const spacer = document.createElement('div');
    spacer.style.flex = '1';

    // Player list (compact dropdown)
    this.playerList = document.createElement('div');
    this.playerList.style.cssText = `
      display: flex;
      gap: 4px;
      overflow-x: auto;
      max-width: 400px;
    `;

    // Exit button (back to menu)
    const exitBtn = document.createElement('button');
    exitBtn.textContent = '\u2715'; // ✕ cross mark
    exitBtn.title = 'Exit Replay (Esc)';
    exitBtn.style.cssText = `
      background: rgba(255,80,80,0.25);
      border: 1px solid rgba(255,80,80,0.5);
      color: #fff;
      font-size: 16px;
      width: 36px;
      height: 36px;
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    exitBtn.addEventListener('click', () => {
      if (this.exitCallback) this.exitCallback();
    });

    controlsRow.appendChild(exitBtn);
    controlsRow.appendChild(this.playPauseBtn);
    controlsRow.appendChild(speedGroup);
    controlsRow.appendChild(this.timeDisplay);
    controlsRow.appendChild(spacer);
    controlsRow.appendChild(this.playerList);

    // Timeline bar
    const timelineWrapper = document.createElement('div');
    timelineWrapper.style.cssText = `
      width: 100%;
      max-width: 900px;
      position: relative;
      height: 20px;
      display: flex;
      align-items: center;
    `;

    this.timelineBar = document.createElement('div');
    this.timelineBar.style.cssText = `
      width: 100%;
      height: 6px;
      background: rgba(255,255,255,0.2);
      border-radius: 3px;
      position: relative;
      cursor: pointer;
    `;

    this.timelineFill = document.createElement('div');
    this.timelineFill.style.cssText = `
      height: 100%;
      background: #ffd700;
      border-radius: 3px;
      width: 0%;
      pointer-events: none;
    `;

    this.timelineHandle = document.createElement('div');
    this.timelineHandle.style.cssText = `
      width: 14px;
      height: 14px;
      background: #fff;
      border-radius: 50%;
      position: absolute;
      top: -4px;
      left: 0%;
      transform: translateX(-50%);
      cursor: grab;
      box-shadow: 0 1px 3px rgba(0,0,0,0.5);
      pointer-events: auto;
    `;

    // Round markers container (dots on timeline)
    this.roundMarkerContainer = document.createElement('div');
    this.roundMarkerContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 100%;
      pointer-events: none;
    `;

    this.timelineBar.appendChild(this.timelineFill);
    this.timelineBar.appendChild(this.roundMarkerContainer);
    this.timelineBar.appendChild(this.timelineHandle);
    timelineWrapper.appendChild(this.timelineBar);

    this.container.appendChild(controlsRow);
    this.container.appendChild(timelineWrapper);
    document.body.appendChild(this.container);

    // Timeline click to seek
    this.timelineBar.addEventListener('mousedown', (e) => {
      this.dragging = true;
      this.seekFromMouseEvent(e);
    });

    this._onMouseMove = (e: MouseEvent) => {
      if (this.dragging) this.seekFromMouseEvent(e);
    };
    this._onMouseUp = () => {
      this.dragging = false;
    };
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._onMouseUp);

    // Keyboard shortcuts
    this._onKeyDown = (e: KeyboardEvent) => {
      if (!this.visible) return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          this.togglePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          this.skip(-SKIP_SECONDS);
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.skip(SKIP_SECONDS);
          break;
        case 'BracketLeft':
          this.cycleSpeed(-1);
          break;
        case 'BracketRight':
          this.cycleSpeed(1);
          break;
        default:
          // Number keys 1-0 to follow players
          if (e.code >= 'Digit1' && e.code <= 'Digit9') {
            const idx = parseInt(e.code.replace('Digit', '')) - 1;
            this.selectPlayerByIndex(idx);
          } else if (e.code === 'Digit0') {
            this.selectPlayerByIndex(9);
          }
          break;
      }
    };
    document.addEventListener('keydown', this._onKeyDown);
  }

  /** Register a callback invoked when the user clicks the exit button. */
  onExit(callback: () => void): void {
    this.exitCallback = callback;
  }

  /** Set the renderer for player follow controls. */
  setRenderer(renderer: ReplayRenderer): void {
    this.renderer = renderer;
  }

  /** Show the replay UI. */
  show(): void {
    this.container.style.display = 'flex';
    this.visible = true;
  }

  /** Hide the replay UI. */
  hide(): void {
    this.container.style.display = 'none';
    this.visible = false;
  }

  /** Whether the UI is currently visible. */
  isVisible(): boolean {
    return this.visible;
  }

  /** Update the UI to reflect current playback state. */
  update(progress: number, currentTime: number, totalTime: number): void {
    // Update timeline position
    const pct = Math.max(0, Math.min(1, progress)) * 100;
    this.timelineFill.style.width = `${pct}%`;
    this.timelineHandle.style.left = `${pct}%`;

    // Update time display
    this.timeDisplay.textContent = `${formatTime(currentTime)} / ${formatTime(totalTime)}`;

    // Update play/pause button
    if (this.replayPlayer.isPlaying() && !this.replayPlayer.isPaused()) {
      this.playPauseBtn.textContent = '\u23F8'; // pause
    } else {
      this.playPauseBtn.textContent = '\u25B6'; // play
    }

    // Update speed display
    this.speedDisplay.textContent = `${this.replayPlayer.getSpeed()}x`;
  }

  /** Build round markers on the timeline. */
  setRoundMarkers(markers: RoundMarker[], totalTicks: number): void {
    this.roundMarkerContainer.innerHTML = '';

    for (const marker of markers) {
      if (totalTicks <= 0) continue;
      const pct = (marker.tick / totalTicks) * 100;
      const dot = document.createElement('div');
      dot.style.cssText = `
        position: absolute;
        left: ${pct}%;
        top: -2px;
        width: 4px;
        height: 10px;
        background: rgba(255,255,255,0.5);
        border-radius: 1px;
        transform: translateX(-50%);
      `;
      dot.title = `Round ${marker.round}`;
      this.roundMarkerContainer.appendChild(dot);
    }
  }

  /** Build player buttons for follow-player controls. */
  setPlayerList(players: Array<{ id: number; name: string; team: number }>): void {
    this.playerList.innerHTML = '';

    for (let i = 0; i < players.length; i++) {
      const p = players[i];
      const btn = document.createElement('button');
      btn.textContent = `${i + 1}`;
      btn.title = p.name;
      const teamColor = p.team === 1 ? '#e8b84b' : '#5b9bd5';
      btn.style.cssText = `
        background: ${teamColor}33;
        border: 1px solid ${teamColor};
        color: #fff;
        font-size: 11px;
        width: 24px;
        height: 24px;
        border-radius: 3px;
        cursor: pointer;
        padding: 0;
      `;
      btn.addEventListener('click', () => {
        if (this.renderer) {
          this.renderer.setFollowTarget(p.id);
        }
      });
      this.playerList.appendChild(btn);
    }
  }

  /** Clean up DOM and event listeners. */
  dispose(): void {
    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
    this.container.remove();
  }

  // ── Internal helpers ──

  private togglePlayPause(): void {
    if (!this.replayPlayer.isPlaying()) {
      this.replayPlayer.play();
    } else if (this.replayPlayer.isPaused()) {
      this.replayPlayer.resume();
    } else {
      this.replayPlayer.pause();
    }
  }

  private cycleSpeed(direction: number): void {
    const currentSpeed = this.replayPlayer.getSpeed();
    const currentIdx = SPEEDS.indexOf(currentSpeed);
    let newIdx: number;
    if (currentIdx === -1) {
      newIdx = 2; // default to 1x
    } else {
      newIdx = Math.max(0, Math.min(SPEEDS.length - 1, currentIdx + direction));
    }
    this.replayPlayer.setSpeed(SPEEDS[newIdx]);
  }

  private skip(seconds: number): void {
    const replay = this.replayPlayer.getReplay();
    if (!replay) return;
    const ticksToSkip = Math.round(seconds * replay.tickRate);
    const currentFrame = this.replayPlayer.getCurrentFrame();
    this.replayPlayer.seekToTick(currentFrame.tick + ticksToSkip);
  }

  private seekFromMouseEvent(e: MouseEvent): void {
    const rect = this.timelineBar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const replay = this.replayPlayer.getReplay();
    if (!replay) return;
    const tick = Math.round(pct * (replay.frames.length - 1));
    this.replayPlayer.seekToTick(tick);
  }

  private selectPlayerByIndex(index: number): void {
    if (!this.renderer) return;
    const replay = this.replayPlayer.getReplay();
    if (!replay || !replay.metadata.players[index]) return;
    this.renderer.setFollowTarget(replay.metadata.players[index].id);
  }

  private createSmallButton(text: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = text;
    btn.style.cssText = `
      background: rgba(255,255,255,0.1);
      border: 1px solid rgba(255,255,255,0.2);
      color: #fff;
      font-size: 10px;
      width: 22px;
      height: 22px;
      border-radius: 3px;
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    return btn;
  }
}

// ── Helpers ──

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
